import { ChainIDType } from '@starsoft/common/models/chain/id';
import { OriginId } from '@starsoft/common/models/origin/id';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useDepositMethods, useKycMeByOriginId } from '../../api';
import { useAccountByCoin } from '../../api/account/queries';
import { useCountries } from '../../api/countries/queries';
import { CreateDepositRequestDto, CreateKycRequestDto } from '../../dto';
import { DepositMethodId, DepositStep, KYCStatus } from '../../enums';
import { useDebounce, useWalletAccountsByCoinType } from '../../hooks';
import { Nullable } from '../../interfaces';
import {
  Account,
  AccountAddress,
  Chain,
  Coin,
  CoinType,
  DepositMethod,
  GenericError,
  Token,
} from '../../models';
import { DepositMapping } from '../../models/deposit/mapping';
import { WalletService } from '../../services';
import { getMoneyOrAmount, isValidChainId } from '../../utils';
import { useUserIdentityValidateByOriginId } from '../user-identity/useUserIdentityValidateByOriginId';
import { useUserOriginId } from '../user-identity/useUserOriginId';
import useDepositSearchParams from './useDepositSearchParams';

/**
 * Custom hook to manage the state of a deposit form.
 * This hook fetches and manages various data required for the deposit form,
 * such as user identity, deposit methods, accounts, countries, and KYC status.
 *
 * @param {UseFormWatch<CreateDepositRequestDto>} watch - Function to watch form fields.
 * @param {UseFormSetValue<CreateDepositRequestDto>} setValue - Function to set form field values.
 * @returns {object} - Returns an object containing various queries and form state.
 *
 * @example
 * const { accountsQuery, depositMethodsQuery, isFormLoading } = useDepositForm(watch, setValue);
 */
export function useDepositForm(
  watch: UseFormWatch<CreateDepositRequestDto & CreateKycRequestDto>,
  setValue: UseFormSetValue<CreateDepositRequestDto & CreateKycRequestDto>,
) {
  // Extract search parameters related to deposit
  const { coinId, coinType, depositMethodId, transactionStep } =
    useDepositSearchParams();

  // Watch specific form fields
  const identifier: string | undefined = watch('identity.identifier');
  const selectedChainId = watch('crypto.chainId');
  const birthDate: string | undefined = watch('identity.birthDate');
  const name: string | undefined = watch('identity.name');
  const chainId = watch('crypto.chainId');
  const originId: Nullable<string> = useUserOriginId();

  // Debounce the watched values to avoid frequent updates
  const debouncedIdentifier = useDebounce(identifier);
  const debouncedName = useDebounce(name);
  const debouncedBirthDate = useDebounce(birthDate);

  /**
   * Function to handle the fetched coin data.
   * It sets the chain ID in the form if the fetched data is valid.
   *
   * @param {Account} data - The fetched account data.
   */
  function handleCoinFetched(data: Account): void {
    if (isValidChainId(data, chainId as any)) {
      return;
    }

    setValue(
      'crypto.chainId',
      data?.coin?.chain != null
        ? (data?.coin?.chain?.id as ChainIDType)
        : (data?.coin?.tokens?.[0]?.chain?.id as ChainIDType),
      { shouldDirty: true },
    );
  }

  // Payload for user identity validation
  const payload = {
    identifier: debouncedIdentifier,
    name: debouncedName ? debouncedName : undefined,
    birthDate: birthDate ? String(debouncedBirthDate) : undefined,
    originId,
  };

  // Query to validate user identity by origin ID
  const {
    data: personalIdentityLookup,
    error: errorPersonalIdentityLookup,
    refetch: refetchPersonalIdentityLookup,
    isLoading: isPendingPersonalIdentityLookup,
  } = useUserIdentityValidateByOriginId({
    payload,
    originId,
    enabled:
      transactionStep == DepositStep.CreatePersonalIdentity &&
      coinType == CoinType.Fiat,
  });

  // Query to fetch deposit methods
  const {
    error: errorDepositMethods,
    refetch: refetchDepositMethods,
    isPending: isPendingDepositMethods,
    depositMethods,
    ...depositMethodsQuery
  } = useDepositMethods({
    enabled: transactionStep == DepositStep.Form,
    coinId,
  });

  // Query to fetch wallet accounts by coin type
  const {
    accounts,
    isLoading: isLoadingAccounts,
    error: errorAccounts,
    refetch: refetchAccounts,
  } = useWalletAccountsByCoinType({
    coinType,
    enabled: transactionStep === DepositStep.Form,
  });

  // Query to fetch countries
  const {
    countries,
    error: errorCountries,
    refetch: refetchCountries,
    isLoading: isLoadingCountries,
    ...rest
  } = useCountries({
    enabled: transactionStep === DepositStep.Form && coinType == CoinType.Fiat,
  });

  // Query to fetch account by coin
  const {
    data: currentAccount,
    error: errorAccountByCoin,
    isLoading: isLoadingAccountByCoin,
    refetch: refetchAccountByCoin,
  } = useAccountByCoin({
    coinId,
    customOnSuccess: handleCoinFetched,
    enabled: transactionStep === DepositStep.Form,
  });

  // Query to fetch KYC status by origin ID
  const {
    kyc,
    error: errorKyc,
    refetch: refetchKyc,
    isPending: isPendingKyc,
  } = useKycMeByOriginId({
    enabled: [DepositStep.Form, DepositStep.CreatePersonalIdentity].includes(
      transactionStep,
    ),
    originId,
  });

  // Determine if the form is loading based on account loading state
  const isFormLoading: boolean = isLoadingAccounts;

  // Aggregate form errors from various queries
  const formError: AxiosError<GenericError> =
    errorDepositMethods ??
    errorAccounts ??
    errorCountries ??
    errorAccountByCoin;

  /**
   * Function to refetch all form-related queries.
   * It refetches deposit methods, accounts, countries, account by coin, and KYC status.
   */
  function refetchForm() {
    if (errorDepositMethods) {
      refetchDepositMethods();
    }

    if (errorAccounts) {
      refetchAccounts();
    }

    if (errorCountries) {
      refetchCountries();
    }

    if (errorAccountByCoin) {
      refetchAccountByCoin();
    }

    if (errorKyc) {
      refetchKyc();
    }
  }

  // Memoized value for the selected deposit method
  const depositMethod: Nullable<DepositMethod> = useMemo(() => {
    if (!depositMethods || depositMethods?.length === 0) {
      return null;
    }

    return depositMethods?.find(
      depositMethod => depositMethod?.id?.toString() === depositMethodId,
    );
  }, [depositMethodId, depositMethods]);

  // Memoized value for the coin mapping based on the selected deposit method
  const coinMapping: DepositMapping = useMemo(
    () =>
      depositMethod?.mappings?.find(
        mapping => mapping?.coin?.id?.toString() === coinId,
      ),
    [depositMethod, coinId],
  );

  // Memoized value for the chain options based on the current account and coin type
  const chainOptions: Chain[] = useMemo(() => {
    if (
      coinType == CoinType.Fiat ||
      originId?.toString() == OriginId.Brazil?.toString()
    ) {
      return [];
    }

    const options =
      currentAccount?.coin?.tokens?.map(token => token.chain) ?? [];

    if (currentAccount?.coin?.chain) {
      options.push(currentAccount?.coin?.chain);
    }

    return options;
  }, [currentAccount, coinType, originId]);

  // Memoized value for the account address based on the current account and selected chain ID
  const accountAddress: Nullable<AccountAddress> = useMemo(() => {
    if (
      !currentAccount ||
      !selectedChainId ||
      originId?.toString() == OriginId.Brazil?.toString()
    ) {
      return null;
    }

    const tokens: Token[] = currentAccount?.coin?.tokens;
    const selectedChain: Chain = chainOptions?.find(
      chain => chain?.id?.toString() === selectedChainId?.toString(),
    );

    return tokens?.length === 0
      ? currentAccount?.addresses?.[0]
      : currentAccount?.addresses?.find(
          ({ format }) => format.id === selectedChain?.format?.id,
        );
  }, [currentAccount, selectedChainId, chainOptions, originId]);

  // Memoized value for the coin options based on the accounts
  const coinOptions: Coin[] = useMemo(() => {
    if (!accounts) return [];

    return accounts.map((account: Account) => account.coin);
  }, [accounts]);

  // Memoized value to check if the coin ID is valid
  const isValidCoinId: boolean = useMemo(() => {
    if (!coinOptions) {
      return false;
    }

    return coinOptions.some(coin => coin.id?.toString() === coinId);
  }, [coinId, coinOptions]);

  // Watch the amount field in the form
  const amount: number | string | bigint = watch('amount');

  // Format the minimum value based on the coin mapping and current account
  const minValueFormatted: string = WalletService.maskCurrency({
    amount: getMoneyOrAmount(coinMapping?.minimum),
    coin: currentAccount?.coin,
  });

  // Format the maximum value based on the coin mapping and current account
  const maxValueFormatted: string = WalletService.maskCurrency({
    amount: getMoneyOrAmount(coinMapping?.maximum),
    coin: currentAccount?.coin,
  });

  // Check if the amount is overvalued based on the coin mapping
  const isOverValued: boolean = !!coinMapping
    ? getMoneyOrAmount(amount?.toString()) >
      getMoneyOrAmount(coinMapping?.maximum)
    : false;

  // Check if the amount is undervalued based on the coin mapping
  const isUnderValued: boolean = !!coinMapping
    ? getMoneyOrAmount(amount?.toString()) <
      getMoneyOrAmount(coinMapping?.minimum)
    : false;

  // Check if the receive amount is valid
  const isValidReceiveAmount: boolean =
    getMoneyOrAmount(amount?.toString()) > 0;

  // Check if the external link should be shown based on the transaction step and KYC status
  const showExternalLink =
    transactionStep == DepositStep.Form &&
    kyc?.status == KYCStatus.Verified &&
    [
      DepositMethodId.BetterbroUpi?.toString(),
      DepositMethodId.PayGamesIndiaUpi?.toString(),
      DepositMethodId.PayGamesSingaporePayNow?.toString(),
      DepositMethodId.PayGamesIndonesiaQris?.toString(),
      DepositMethodId.PayGamesSouthAfrica?.toString(),
      DepositMethodId.PayGamesNigeria?.toString(),
    ].includes(depositMethodId?.toString());

  // Memoized value for the current chain based on the chain options and chain ID
  const currentChain = useMemo(
    () =>
      chainOptions?.find(chain => chain?.id?.toString() == chainId?.toString()),
    [chainOptions, chainId],
  );

  // Return the form state and queries
  return {
    personalIdentityLookUpQuery: {
      personalIdentityLookup,
      errorPersonalIdentityLookup,
      refetchPersonalIdentityLookup,
      isPendingPersonalIdentityLookup,
    },
    depositMethodsQuery: {
      errorDepositMethods,
      refetchDepositMethods,
      isPendingDepositMethods,
      depositMethods,
      ...depositMethodsQuery,
    },
    accountsQuery: {
      accounts,
      isLoadingAccounts,
      errorAccounts,
      refetchAccounts,
    },
    countriesQuery: {
      countries,
      errorCountries,
      refetchCountries,
      isLoadingCountries,
      ...rest,
    },
    accountByCoinQuery: {
      currentAccount,
      accountAddress,
      errorAccountByCoin,
      isLoadingAccountByCoin,
      refetchAccountByCoin,
    },
    useKycMeByOriginIdQuery: {
      kyc,
      errorKyc,
      refetchKyc,
      isPendingKyc,
    },
    formError,
    refetchForm,
    isFormLoading,
    coinMapping,
    depositMethod,
    coinOptions,
    chainOptions,
    isOverValued,
    isUnderValued,
    maxValueFormatted,
    minValueFormatted,
    isValidReceiveAmount,
    showExternalLink,
    isValidCoinId,
    currentChain,
  };
}
